import React, { useEffect, useState, useRef, useCallback } from "react";

// ایمپورت ابزارهای کمکی و API
import { getStories } from "../api/HomeApi";
import { showError } from "../../../utils/toastHelper";

const STORY_DURATION = 5000; // ms per story

// ─── Story Viewer (fullscreen overlay) ───────────────────────────────────────
const StoryViewer = ({ stories, startIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const [videoReady, setVideoReady] = useState(false);

  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const elapsedRef = useRef(0);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const videoRef = useRef(null);

  const story = stories[currentIndex];
  const isVideo = !!story?.video;

  // ── timer logic ──
  const startTimer = useCallback(
    (fromElapsed = 0) => {
      clearInterval(timerRef.current);
      startTimeRef.current = Date.now() - fromElapsed;

      timerRef.current = setInterval(() => {
        const elapsed = Date.now() - startTimeRef.current;
        const pct = Math.min((elapsed / STORY_DURATION) * 100, 100);
        setProgress(pct);

        if (elapsed >= STORY_DURATION) {
          clearInterval(timerRef.current);
          goNext();
        }
      }, 30);
    },
    [currentIndex],
  );

  const pauseTimer = () => {
    clearInterval(timerRef.current);
    elapsedRef.current = Date.now() - startTimeRef.current;
    setPaused(true);
    if (videoRef.current) videoRef.current.pause();
  };

  const resumeTimer = () => {
    setPaused(false);
    startTimer(elapsedRef.current);
    if (videoRef.current) videoRef.current.play();
  };

  const resetAndGo = (index) => {
    clearInterval(timerRef.current);
    elapsedRef.current = 0;
    setProgress(0);
    setVideoReady(false);
    setCurrentIndex(index);
  };

  const goNext = useCallback(() => {
    if (currentIndex < stories.length - 1) {
      resetAndGo(currentIndex + 1);
    } else {
      onClose();
    }
  }, [currentIndex, stories.length, onClose]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) {
      resetAndGo(currentIndex - 1);
    } else {
      resetAndGo(0); // restart first
    }
  }, [currentIndex]);

  // start timer when story changes
  useEffect(() => {
    if (!isVideo || videoReady) {
      startTimer(0);
    }
    return () => clearInterval(timerRef.current);
  }, [currentIndex, videoReady]);

  // keyboard nav: ArrowLeft = next story, ArrowRight = prev story
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") goNext();
      if (e.key === "ArrowRight") goPrev();
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goNext, goPrev, onClose]);

  // touch swipe: swipe right = next story, swipe left = prev story
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
      if (dx > 0)
        goNext(); // swipe راست → استوری بعدی
      else goPrev(); // swipe چپ  → استوری قبلی
    }
    touchStartX.current = null;
  };

  // tap zones: left = next story, right = prev story, center = pause/resume
  const onTap = (e) => {
    const x = e.clientX;
    const w = window.innerWidth;
    if (x < w * 0.3)
      goNext(); // چپ صفحه = بعدی
    else if (x > w * 0.7)
      goPrev(); // راست صفحه = قبلی
    else paused ? resumeTimer() : pauseTimer();
  };

  return (
    <div
      style={styles.overlay}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onClick={onTap}
    >
      {/* ── Progress bars — RTL: استوری‌های قبلی از راست پر می‌شوند ── */}
      <div style={styles.progressRow} dir="rtl">
        {stories.map((_, i) => (
          <div key={i} style={styles.progressTrack}>
            <div
              style={{
                ...styles.progressFill,
                width:
                  i < currentIndex
                    ? "100%"
                    : i === currentIndex
                      ? `${progress}%`
                      : "0%",
              }}
            />
          </div>
        ))}
      </div>

      {/* ── Header ── */}
      <div style={styles.header} onClick={(e) => e.stopPropagation()}>
        <div style={styles.vendorRow}>
          <img src={story.vendor?.image} alt="" style={styles.vendorAvatar} />
          <div>
            <div style={styles.vendorName}>{story.vendor?.title}</div>
            <div style={styles.storyTitle}>{story.title}</div>
          </div>
        </div>
        <button style={styles.closeBtn} onClick={onClose} aria-label="بستن">
          ✕
        </button>
      </div>

      {/* ── Media ── */}
      <div style={styles.mediaWrap}>
        {isVideo ? (
          <video
            ref={videoRef}
            src={story.video}
            style={styles.media}
            autoPlay
            playsInline
            muted={false}
            onCanPlay={() => setVideoReady(true)}
            onEnded={goNext}
          />
        ) : (
          <img src={story.cover} alt={story.title} style={styles.media} />
        )}
      </div>

      {/* ── ناحیه‌های کلیک: چپ = بعدی، راست = قبلی ── */}
      <div
        style={styles.tapLeft}
        onClick={(e) => {
          e.stopPropagation();
          goNext();
        }}
      />
      <div
        style={styles.tapRight}
        onClick={(e) => {
          e.stopPropagation();
          goPrev();
        }}
      />
    </div>
  );
};

// ─── Story Thumbnail Card ─────────────────────────────────────────────────────
const StoryCard = ({ story, onClick }) => (
  <div style={styles.cardWrapper} onClick={onClick}>
    <div style={styles.card}>
      {story.cover ? (
        <img src={story.cover} style={styles.cardBg} alt={story.title} />
      ) : (
        <video
          src={story.video + "#t=0.1"}
          style={styles.cardBg}
          preload="metadata"
          muted
        />
      )}
      <div style={styles.cardGradient} />
      <div style={styles.cardTop}>
        <div style={styles.avatarRing}>
          <img src={story.vendor?.image} style={styles.cardAvatar} alt="" />
        </div>
      </div>
      <div style={styles.cardBottom}>
        <span style={styles.cardTitle}>{story.title}</span>
      </div>
    </div>
  </div>
);

// ─── Main Stories Component ───────────────────────────────────────────────────
const Stories = ({ vendorId = null }) => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [startIndex, setStartIndex] = useState(0);

  useEffect(() => {
    const fetchStories = async () => {
      setLoading(true);
      try {
        const params = { vendor_id: vendorId };
        const response = await getStories(params);
        setStories(response?.data || []);
      } catch (err) {
        showError("خطا در بارگذاری استوری‌ها.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchStories();
  }, []);

  const openViewer = (index) => {
    setStartIndex(index);
    setViewerOpen(true);
  };

  if (loading)
    return <div style={styles.loadingText}>در حال بارگذاری استوری‌ها...</div>;
  if (!stories.length) return null;

  return (
    <>
      <style>{globalCSS}</style>

      <div style={styles.container} dir="rtl">
        <div style={styles.sectionLabel}>استوری ها</div>
        <div style={styles.scrollRow}>
          {stories.map((story, i) => (
            <StoryCard
              key={story.id}
              story={story}
              onClick={() => openViewer(i)}
            />
          ))}
        </div>
      </div>

      {viewerOpen && (
        <StoryViewer
          stories={stories}
          startIndex={startIndex}
          onClose={() => setViewerOpen(false)}
        />
      )}
    </>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = {
  // List
  container: { padding: "8px 0 12px", direction: "rtl" },
  sectionLabel: {
    fontFamily: "inherit",
    fontSize: 15,
    fontWeight: 700,
    color: "#1a1a1a",
    padding: "0 16px 10px",
    direction: "rtl",
  },
  scrollRow: {
    display: "flex",
    flexDirection: "row",
    overflowX: "auto",
    padding: "0 12px 4px",
    gap: 10,
    scrollbarWidth: "none",
    msOverflowStyle: "none",
  },
  loadingText: { textAlign: "center", padding: 16, color: "#888" },

  // Card
  cardWrapper: { flexShrink: 0, cursor: "pointer" },
  card: {
    position: "relative",
    width: 110,
    height: 170,
    borderRadius: 16,
    overflow: "hidden",
    boxShadow: "0 2px 12px rgba(0,0,0,0.18)",
    border: "2.5px solid transparent",
    background:
      "linear-gradient(white, white) padding-box, linear-gradient(135deg, #f97316, #a855f7) border-box",
    transition: "transform 0.18s",
  },
  cardBg: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  cardGradient: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(to bottom, rgba(0,0,0,0.28) 0%, transparent 40%, rgba(0,0,0,0.55) 100%)",
  },
  cardTop: {
    position: "absolute",
    top: 8,
    left: 0,
    right: 0,
    display: "flex",
    justifyContent: "center",
  },
  avatarRing: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #f97316, #a855f7)",
    padding: 2.5,
    boxShadow: "0 1px 6px rgba(0,0,0,0.3)",
  },
  cardAvatar: {
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    border: "2px solid white",
    objectFit: "cover",
  },
  cardBottom: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    padding: "6px 6px 8px",
    display: "flex",
    justifyContent: "center",
  },
  cardTitle: {
    color: "#fff",
    fontSize: 11.5,
    fontWeight: 600,
    textAlign: "center",
    textShadow: "0 1px 4px rgba(0,0,0,0.7)",
    direction: "rtl",
    lineHeight: 1.3,
  },

  // Viewer overlay
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    background: "#000",
    display: "flex",
    flexDirection: "column",
    userSelect: "none",
    touchAction: "pan-y",
  },
  progressRow: {
    display: "flex",
    gap: 4,
    padding: "12px 12px 0",
    position: "relative",
    zIndex: 2,
  },
  progressTrack: {
    flex: 1,
    height: 3,
    borderRadius: 3,
    background: "rgba(255,255,255,0.35)",
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    background: "#fff",
    borderRadius: 3,
    transition: "width 0.03s linear",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 14px 6px",
    position: "relative",
    zIndex: 2,
  },
  vendorRow: { display: "flex", alignItems: "center", gap: 10 },
  vendorAvatar: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    border: "2px solid rgba(255,255,255,0.8)",
    objectFit: "cover",
  },
  vendorName: {
    color: "#fff",
    fontWeight: 700,
    fontSize: 13.5,
    lineHeight: 1.2,
  },
  storyTitle: { color: "rgba(255,255,255,0.75)", fontSize: 11.5, marginTop: 1 },
  closeBtn: {
    background: "none",
    border: "none",
    color: "#fff",
    fontSize: 22,
    cursor: "pointer",
    lineHeight: 1,
    padding: 4,
    opacity: 0.85,
  },

  mediaWrap: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  media: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    maxHeight: "100%",
  },

  // invisible tap zones
  tapLeft: {
    position: "absolute",
    left: 0,
    top: "10%",
    bottom: "10%",
    width: "28%",
    zIndex: 3,
    cursor: "pointer",
  },
  tapRight: {
    position: "absolute",
    right: 0,
    top: "10%",
    bottom: "10%",
    width: "28%",
    zIndex: 3,
    cursor: "pointer",
  },
};

const globalCSS = `
  .stories-scroll-row::-webkit-scrollbar { display: none; }
  @media (prefers-reduced-motion: reduce) {
    * { transition: none !important; animation: none !important; }
  }
  @media (min-width: 768px) {
    /* wider cards on tablet/desktop */
  }
`;

export default Stories;
